<template>
    <div>
        <card-header title="Task Details" :subheader="title" icon="fa-tasks"/>

        <card-body>
            <card-list>
                <task-list-item
                    v-for="task in tasks"
                    :card="card"
                    :task="task"
                    :key="task.id"
                    :active="isActive(task)"
                    :context="contextLine(task)"
                    @click="openEntity(task)"
                />
                <container-list-item padded v-if="!tasks.length">
                    <p class="has-text-grey is-size-7 my-2">No outstanding tasks.</p>
                </container-list-item>
            </card-list>
        </card-body>
    </div>
</template>

<script>
    import CardHeader from "../../TIER/components/CardHeader";
    import CardBody from "../../TIER/components/CardBody";
    import CardList from "../../TIER/components/CardList";
    import ContainerListItem from "../../TIER/components/ContainerListItem";
    import {client as http} from "../../http_client";
    import TaskListItem from "@/components/TaskListItem";

    const TITLES = {
        'overdue': 'Overdue',
        'customer': 'Customers',
        'lead': 'Leads',
        'contact': 'Contacts',
        'trip': 'Trips',
        'registration': 'Registrations',
        'flights': 'Registration Flights',
        'reg-hotels': 'Registration Hotels',
        'reg-events': 'Registration Events',
        'reg-addons': 'Registration Add-Ons',
        'trip-events': 'Trip Events',
        'trip-hotels': 'Trip Hotels',
        'trip-addons': 'Trip Add-Ons'
    };

    export default {
        components: {TaskListItem, ContainerListItem, CardList, CardBody, CardHeader},
        props: ['card', 'props', 'child'],
        data: function () {
            return {
                title: '',
                tasks: []
            };
        },
        methods: {
            loadTasks() {
                this.$emit('loading', true);
                // No assignee prop -> your own queue (the API defaults to the
                // logged-in user). Otherwise a staff member's id or
                // 'unassigned' for the pool nobody has picked up.
                const query = this.props.assignee ? '?assignee=' + this.props.assignee : '';
                http.get('/api/tasks/' + this.props.type + query, {force: true}).then(response => {
                    this.tasks = response.data;
                    this.$emit('loading', false);
                });
            },
            // Clicking a task opens the card of the entity that owns it; the
            // Tasks tab there holds the task itself. Keyed off the task's own
            // entity_type (not props.type) so mixed lists like Overdue work.
            entityTarget(task) {
                switch (task.entity_type) {
                    case 'customer':
                        return ['customer', {userId: task.entity_id}];
                    case 'lead':
                        return ['lead', {leadId: task.entity_id}];
                    case 'contact':
                        return ['contact', {contactId: task.entity_id}];
                    case 'trip':
                        return ['trip', {tripId: task.entity_id}];
                    case 'registration':
                        return ['registration', {registrationId: task.entity_id}];
                    case 'flights':
                        return ['edit-flight', {registrationId: task.registration_id, flightId: task.entity_id}];
                    case 'reg-hotels':
                        return ['edit-reg-hotel', {registrationId: task.registration_id, hotelId: task.entity_id}];
                    case 'reg-addons':
                        return ['edit-addon', {registrationId: task.registration_id, addonId: task.entity_id}];
                    case 'reg-events':
                        return task.day_event_id
                            ? ['edit-override-event', {registrationId: task.registration_id, dayId: task.day_id, eventId: task.entity_id}]
                            : ['edit-custom-event', {registrationId: task.registration_id, dayId: task.day_id, eventId: task.entity_id}];
                    case 'trip-events':
                        return ['edit-event', {tripId: task.trip_id, dayId: task.day_id, eventId: task.entity_id}];
                    case 'trip-hotels':
                        return ['edit-trip-hotel', {tripId: task.trip_id, hotelId: task.entity_id}];
                    case 'trip-addons':
                        return ['trip-addon', {addonId: task.entity_id}];
                }
            },
            openEntity(task) {
                const target = this.entityTarget(task);
                if (target) this.$openCard(target[0], target[1], this.card);
            },
            contextLine(task) {
                // Dedupe: trip/registration tasks repeat the trip title as
                // their entity title.
                return [...new Set([task.entity_title, task.owner_name, task.trip_title].filter(Boolean))]
                    .join(' · ');
            },
            isActive(task) {
                const target = this.entityTarget(task);
                if (!target || !this.child) return false;
                if (this.child.definition.component !== target[0]) return false;
                // The entity id prop is the last key in the target props;
                // card props round-trip as strings.
                const idKey = Object.keys(target[1]).pop();
                return String(this.child.props[idKey]) === String(target[1][idKey]);
            },
            reload() {
                this.loadTasks();
            }
        },
        watch: {
            'props.type': function() {
                this.title = TITLES[this.props.type] || '';
                this.$emit('title', this.title);
                this.loadTasks();
            },
            // Same type, different queue (e.g. hopping between two staff
            // members' flight lists) reuses this card -- reload.
            'props.assignee': function() {
                this.loadTasks();
            }
        },
        mounted() {
            this.title = TITLES[this.props.type] || '';
            this.$emit('title', this.title);
            this.loadTasks();
        }
    };
</script>

<style lang="scss" scoped>
</style>
